import { User } from "oidc-client";

export enum AuthenticationState {
  /** User has not attempted authentication */
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  AUTHENTICATING = 'AUTHENTICATING',
  AUTHENTICATED = 'AUTHENTICATED',
  REFRESHING_TOKEN = 'REFRESHING_TOKEN',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  LOGGING_OUT = 'LOGGING_OUT'
}
export interface AuthenticatedUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly givenName?: string;
  readonly familyName?: string;
  readonly profilePictureUrl?: string;
  readonly organizationId?: string;
  readonly tenantId?: string;
  readonly roles?: string[];
  readonly permissions?: string[];
  readonly authenticatedAt: string;
  readonly expiresAt: string;
  readonly identityProvider: string;
  readonly metadata?: Record<string, any>;
  readonly customClaims?: Record<string, any>;
  readonly profile?: Record<string, any>;
  readonly access_token?: string;
}
export interface AccessToken {
  readonly value: string;
  readonly type: string;
  readonly expiresAt: Date | string;
  readonly scopes: string[];
  readonly resource?: string;
}

export interface AuthenticationResult {
  readonly user: AuthenticatedUser;
  readonly accessToken: AccessToken;
  readonly isSilentRenewal: boolean;
  readonly state: AuthenticationState;
}

export interface LoginOptions {
  readonly suggestedUser?: string;
  readonly forceInteractive?: boolean;
  readonly prompt?: 'login' | 'select_account' | 'consent' | 'none';
  readonly additionalScopes?: string[];
  readonly extraParams?: Record<string, string>;
  readonly redirectUrl?: string;
}

export interface LogoutOptions {
  readonly redirectUrl?: string;
  readonly clearAllData?: boolean;
}

export interface TokenRenewalOptions {
  readonly forceRenewal?: boolean;
  readonly resource?: string;
  readonly scopes?: string[];
}

export interface AuthenticationStateChangeEvent {
  readonly previousState: AuthenticationState;
  readonly currentState: AuthenticationState;
  readonly user?: AuthenticatedUser;
  readonly error?: AuthenticationError;
  readonly timestamp: string;
}

export class AuthenticationError extends Error {
  readonly code: AuthenticationErrorCode;
  readonly details?: Record<string, any>;
  readonly retryable: boolean;
  readonly originalError?: Error;
  
  constructor(
    code: AuthenticationErrorCode,
    message: string,
    retryable: boolean = false,
    details?: Record<string, any>,
    originalError?: Error
  ) {
    super(message);
    this.name = 'AuthenticationError';
    this.code = code;
    this.retryable = retryable;
    this.details = details;
    this.originalError = originalError;
  }
}

export enum AuthenticationErrorCode {
  USER_CANCELLED = 'USER_CANCELLED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
  MFA_REQUIRED = 'MFA_REQUIRED',
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_REFRESH_FAILED = 'TOKEN_REFRESH_FAILED',
  INVALID_TOKEN = 'INVALID_TOKEN',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  ACCESS_DENIED = 'ACCESS_DENIED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  CONSENT_REQUIRED = 'CONSENT_REQUIRED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}
